import React, { useEffect, useState } from 'react';
import hrApi from '../api';
import { formatDisplayName } from '../utils/formatTime';

function formatDateStr(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function statusBadge(status) {
  const c = status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning';
  const label = status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : 'Pending';
  return <span className={`badge bg-${c}${status === 'pending' ? ' text-dark' : ''}`}>{label}</span>;
}

function displayReason(r) {
  if (r.reason != null && String(r.reason).trim()) return String(r.reason);
  const legacy = [r.comment, r.fromTime && r.toTime ? `${r.fromTime}–${r.toTime}` : null].filter(Boolean).join(' · ');
  return legacy || '–';
}

function OvertimeRequestList({
  requests: initialRequests,
  onUpdate,
  showEmployeeName = false,
  onView,
  onApprove,
  onReject,
  canDecide = true,
  decisionLoadingId = null,
  focusRequestId = null,
}) {
  const [requests, setRequests] = useState(initialRequests || []);
  const [filter, setFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setRequests(initialRequests || []);
  }, [initialRequests]);

  useEffect(() => {
    if (!focusRequestId) return;
    const id = String(focusRequestId);
    const timer = window.setTimeout(() => {
      const el = document.getElementById(`overtime-row-${id}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    return () => window.clearTimeout(timer);
  }, [focusRequestId, requests]);

  const filtered = filter === 'all' ? requests : requests.filter((r) => r.status === filter);

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this overtime request?')) return;
    setDeletingId(id);
    try {
      await hrApi.delete(`/api/overtime/${id}`);
      setRequests((prev) => prev.filter((r) => r._id !== id));
      if (onUpdate) onUpdate();
    } catch (_) {
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map((f) => (
          <button
            key={f}
            type="button"
            className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="text-muted small">No overtime requests.</p>
      ) : (
        <div className="hr-allowance-table-wrap">
          <div className="table-responsive">
            <table className="table table-sm hr-allowance-table mb-0">
              <thead>
                <tr>
                  {showEmployeeName && <th>Employee</th>}
                  <th>Status</th>
                  <th>Applied on</th>
                  <th>Reason for overtime</th>
                  <th>Granted</th>
                  {filter === 'rejected' && <th>Rejection reason</th>}
                  {(showEmployeeName || filter === 'pending') && (
                    <th className="text-end" style={showEmployeeName ? { minWidth: 200 } : undefined}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r._id}
                    id={`overtime-row-${r._id}`}
                    className={focusRequestId && String(focusRequestId) === String(r._id) ? 'table-warning' : ''}
                  >
                    {showEmployeeName && (
                      <td className="hr-allowance-name">
                        {r.adminId && typeof r.adminId === 'object' ? formatDisplayName(r.adminId.username) : r.adminId || '–'}
                      </td>
                    )}
                    <td>{statusBadge(r.status)}</td>
                    <td>{formatDateStr(r.createdAt)}</td>
                    <td className="small" style={{ maxWidth: '320px', whiteSpace: 'pre-wrap' }}>{displayReason(r)}</td>
                    <td className="small">
                      {String(r?.status || '').toLowerCase() === 'approved' && r.approvedDays != null && Number.isFinite(Number(r.approvedDays))
                        ? r.approvedDays
                        : '–'}
                    </td>
                    {filter === 'rejected' && <td className="small">{r.rejectedReason || '–'}</td>}
                    {(showEmployeeName || filter === 'pending') && (
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          {showEmployeeName ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => onView && onView(r)}
                                disabled={decisionLoadingId === r._id}
                              >
                                View
                              </button>
                              {canDecide && r.status === 'pending' && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={() => onApprove && onApprove(r)}
                                    disabled={decisionLoadingId === r._id}
                                  >
                                    {decisionLoadingId === r._id ? 'Processing…' : 'Approve'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => onReject && onReject(r)}
                                    disabled={decisionLoadingId === r._id}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </>
                          ) : (
                            r.status === 'pending' && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDelete(r._id)}
                                disabled={deletingId === r._id}
                              >
                                {deletingId === r._id ? '…' : 'Cancel'}
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default OvertimeRequestList;
